import { Card as SDSCard } from '@s-group/design-system-components/Card/Card'
import { TertiaryHeading } from '@ui-components/typography/headings/TertiaryHeading'
import { BodyText, TextProps } from '@ui-components/typography/Typography'
import { isValidElement, ReactElement, useId } from 'react'
import { styled } from 'styled-components'

import { BulletIcon, CheckIcon, IconContainer } from '../assets/Icons'
import { Card, CardTitle, WithButtonClick, WithContentClick } from '../containers/Card'
import { SPACE_SMALL, SPACE_XXSMALL } from '../global/spacing'
import { SecondaryHeading } from '../typography/headings/SecondaryHeading'
import { toObjArray } from '../utilities/helpers'
import { CompletionProgress, Phase, PhaseContent } from './PhasesView'

export type PhaseInstructions = Record<CompletionProgress, PhaseHeaderTexts>

export interface SimplePhasesViewProps {
  phasesHeader: string
  phases: Phase[]
  shouldShowCompletedPhases: boolean
}

export interface PhaseHeaderTexts {
  title: string
  text: string
}

const DashConnector = styled.div`
  margin: 0px 0 -15px 20px;
  border-left: 1px dashed currentColor;
  height: 15px;
  color: inherit;
`

const PhaseWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
const PhaseIcon = styled(IconContainer)`
  width: 42px;
`

const PhasesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: start;
  gap: 15px;
  margin-top: 0px;
`

const PhasesSection = styled.section`
  max-width: 700px;
`

const PhasesTitle = styled.div`
  margin-bottom: ${SPACE_XXSMALL};
  h2 {
    color: inherit;
  }
`

const PhaseContainer = styled.div``

const PhaseCard = styled(Card)`
  max-width: unset;
`

const DescriptionText = styled(BodyText)`
  display: inline-block;
  &:not(:first-of-type) {
    margin-top: ${SPACE_XXSMALL};
  }
`

const ContentCard = styled(SDSCard)`
  padding: ${SPACE_SMALL};
`

const PhaseText = styled(BodyText)<TextProps>`
  color: inherit;
`

const isStandardPhaseContent = (phase: Phase) => {
  return !isValidElement(phase.content)
}

export const PhaseView = (props: { phase: Phase }) => {
  const { phase } = props
  const titleId = useId()

  if (!isStandardPhaseContent(phase)) {
    return null
  }

  const phaseContent = phase.content as PhaseContent

  const textArray = (Array.isArray(phaseContent.text) ? phaseContent.text : [phaseContent.text]).filter(Boolean)
  const textObjArr = toObjArray([...textArray])
  const description =
    textArray.length > 0
      ? textObjArr.map((paragraph) => <DescriptionText key={paragraph.id}>{paragraph.item}</DescriptionText>)
      : undefined

  const title: CardTitle = { text: phase.header, $tagName: 'h3', $variant: 'heading' }
  const subtitle = phaseContent.description ? { text: phaseContent.description } : undefined

  let titleProps
  if (phase.isFinalizationPhase) {
    titleProps = {
      heading: {
        'aria-labelledby': titleId,
        title: title.text,
        component: <SecondaryHeading header={title.text} tagName={title.$tagName} id={titleId} />,
      },
    }
  } else {
    titleProps = { title }
  }

  if (phaseContent.action) {
    const withButtonClick: WithButtonClick = {
      kind: 'button',
      buttonText: phaseContent.action.label ?? '',
      onClick: phaseContent.action.onClick,
    }
    const withContentClick: WithContentClick = {
      kind: 'content',
      onClick: phaseContent.action.onClick,
    }
    const clickOptions = phaseContent.action.kind === 'content' ? withContentClick : withButtonClick

    return (
      <PhaseCard
        icon={phaseContent.icon}
        variant="primary"
        subtitle={subtitle}
        description={description}
        clickOptions={clickOptions}
        {...titleProps}
      />
    )
  } else {
    return <PhaseCard variant="basic" subtitle={subtitle} description={description} {...titleProps} />
  }
}

const PhaseContentComponent = (props: { phaseToShow: Phase; allPhases: Phase[] }) => {
  const { phaseToShow, allPhases } = props
  const isComplete = phaseToShow.isComplete
  const isFirstIncomplete = phaseToShow === allPhases.find((phase) => !phase.isComplete)
  const isPhaseStandardContent = isStandardPhaseContent(phaseToShow)

  const id = useId()
  const component = isPhaseStandardContent ? (
    <PhaseView phase={phaseToShow} key={phaseToShow.header} />
  ) : (
    <ContentCard variant="elevated">{phaseToShow.content as ReactElement}</ContentCard>
  )

  return (
    <PhaseContainer id={id} data-testid="phase">
      {!isFirstIncomplete && (
        <PhaseWrapper>
          <PhaseIcon>{isComplete ? <CheckIcon /> : <BulletIcon />}</PhaseIcon>
          <PhaseText id={`${id}-header`}>{phaseToShow.header}</PhaseText>
        </PhaseWrapper>
      )}
      {isFirstIncomplete && component}
      <DashConnector />
    </PhaseContainer>
  )
}

export const SimplePhasesView = (props: SimplePhasesViewProps) => {
  const { phases, phasesHeader, shouldShowCompletedPhases } = props

  const completePhaseCount = phases.filter((phase) => phase.isComplete).length
  const incompletePhaseCount = phases.length - completePhaseCount

  let phasesText = phasesHeader
  if (!shouldShowCompletedPhases) {
    phasesText += ` (${incompletePhaseCount})`
  }

  return (
    <PhasesSection>
      <PhasesTitle>
        <TertiaryHeading header={phasesText} tagName="h2" />
      </PhasesTitle>
      <PhasesWrapper>
        <DashConnector />
        {phases
          .filter((phase) => shouldShowCompletedPhases || !phase.isComplete)
          .map((phaseToShow) => (
            <PhaseContentComponent allPhases={phases} phaseToShow={phaseToShow} key={phaseToShow.header} />
          ))}
      </PhasesWrapper>
    </PhasesSection>
  )
}
